import React, { useState } from "react";
import { Factory, Beaker, ShieldCheck, Leaf, Globe2, FlaskConical } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

import { LANGUAGES, CONTENT } from "../mock/siteContent";
import axios from "axios";

const BRAND_GREEN = "#7A9F3A";

const LanguageSwitcher = ({ current, onChange }) => {
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-white/70 p-1 shadow-sm ring-1 ring-slate-200 backdrop-blur-xl">
      {LANGUAGES.map((lang) => (
        <button
          key={lang.code}
          type="button"
          onClick={() => onChange(lang.code)}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors duration-150 ${
            current === lang.code
              ? "bg-" + BRAND_GREEN + " text-white shadow-sm"
              : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {lang.label}
        </button>
      ))}
    </div>
  );
};

const Header = ({ language, onLanguageChange, t }) => {
  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-2xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900/3">
            <img src="/ht-logo.png" alt="PT Huatong Trading Indonesia" className="h-9 w-9 object-contain" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold tracking-tight text-slate-900">
              {t.meta.companyName}
            </span>
            <span className="text-[11px] text-slate-500">Filler Masterbatch CaCO₃ & Na₂SO₄</span>
          </div>
        </div>

        <nav className="hidden items-center gap-6 text-xs font-medium text-slate-600 md:flex">
          <a href="#solutions" className="hover:text-slate-900 transition-colors">
            {language === "id" && "Solusi"}
            {language === "en" && "Solutions"}
            {language === "zh" && "解决方案"}
          </a>
          <a href="#applications" className="hover:text-slate-900 transition-colors">
            {language === "id" && "Aplikasi"}
            {language === "en" && "Applications"}
            {language === "zh" && "应用领域"}
          </a>
          <a href="#technology" className="hover:text-slate-900 transition-colors">
            {language === "id" && "Teknologi"}
            {language === "en" && "Technology"}
            {language === "zh" && "技术优势"}
          </a>
          <a href="#faq" className="hover:text-slate-900 transition-colors">
            {language === "id" && "FAQ"}
            {language === "en" && "FAQ"}
            {language === "zh" && "常见问题"}
          </a>
          <a href="#contact" className="hover:text-slate-900 transition-colors">
            {language === "id" && "Kontak"}
            {language === "en" && "Contact"}
            {language === "zh" && "联系我们"}
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher current={language} onChange={onLanguageChange} />
          <Button
            asChild
            size="sm"
            style={{ backgroundColor: BRAND_GREEN }}
            className="hidden rounded-full px-4 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-lime-600 md:inline-flex"
          >
            <a href="https://wa.me/6282289309196" target="_blank" rel="noopener noreferrer">
              {t.hero.primaryCta}
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

const HeroSection = ({ t }) => {
  const [activeGrade, setActiveGrade] = React.useState("caco3");

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveGrade((prev) => (prev === "caco3" ? "na2so4" : "caco3"));
    }, 5200);
    return () => clearInterval(interval);
  }, []);

  const isCaCO3 = activeGrade === "caco3";
  const imageSrc = isCaCO3 ? "/caco3-hero.jpg" : "/na2so4-hero.jpg";
  const chipLabel = isCaCO3 ? "CaCO₃ Compounding Line" : "Na₂SO₄ Compounding Line";

  return (
    <section className="relative overflow-hidden border-b border-slate-200 bg-white text-slate-900">
      <div className="pointer-events-none absolute inset-0 opacity-80 hero-gradient-light" aria-hidden="true" />
      <div className="relative mx-auto flex max-w-6xl flex-col gap-10 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:py-20 lg:px-8">
        <div className="flex-1 space-y-6">
          <Badge
            className="border backdrop-blur-xl"
            style={{
              backgroundColor: "rgba(122, 159, 58, 0.06)",
              borderColor: "rgba(122, 159, 58, 0.35)",
              color: BRAND_GREEN,
            }}
          >
            {t.meta.tagline}
          </Badge>
          <div className="space-y-4">
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              {t.hero.title}
            </h1>
            <p className="max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
              {t.hero.subtitle}
            </p>
          </div>
          <ul className="space-y-2 text-sm text-slate-700">
            {t.hero.bullets.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span
                  className="mt-1 h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: BRAND_GREEN }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              asChild
              className="rounded-full px-6 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-lime-600"
              style={{ backgroundColor: BRAND_GREEN }}
            >
              <a href="https://wa.me/6282289309196" target="_blank" rel="noopener noreferrer">
                {t.hero.primaryCta}
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full border-slate-300 bg-white/70 px-5 text-xs font-semibold text-slate-800 hover:bg-slate-50"
            >
              <a href="/hti-product-brochure-2025.pdf" target="_blank" rel="noopener noreferrer">
                {t.hero.secondaryCta}
              </a>
            </Button>
          </div>
        </div>

        <div className="flex-1">
          <div className="glass-surface-light relative mx-auto max-w-md rounded-3xl p-5 shadow-xl">
            <div className="mb-3 inline-flex gap-1 rounded-full bg-slate-100 p-1 text-[11px] font-medium text-slate-600">
              <button
                type="button"
                onClick={() => setActiveGrade("caco3")}
                className={`rounded-full px-3 py-1 transition-all ${
                  isCaCO3 ? "bg-slate-900 text-white shadow-sm" : "hover:text-slate-900"
                }`}
              >
                CaCO₃ Grade
              </button>
              <button
                type="button"
                onClick={() => setActiveGrade("na2so4")}
                className={`rounded-full px-3 py-1 transition-all ${
                  !isCaCO3 ? "bg-slate-900 text-white shadow-sm" : "hover:text-slate-900"
                }`}
              >
                Na₂SO₄ Grade
              </button>
            </div>

            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
              <div className="aspect-[4/3] w-full overflow-hidden">
                <img
                  src={imageSrc}
                  alt={isCaCO3 ? "CaCO₃ filler masterbatch" : "Na₂SO₄ filler masterbatch"}
                  className="h-full w-full object-cover align-middle transition-all duration-700 ease-out hero-pellet-image"
                />
              </div>
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0,rgba(122,159,58,0.04),transparent_55%),radial-gradient(circle_at_80%_100%,rgba(190,242,100,0.12),transparent_55%)]" />
              <div className="absolute inset-0 flex flex-col justify-between p-4">
                <div className="flex items-center justify-between text-[11px] text-slate-700">
                  <span>{chipLabel}</span>
                  <span className="rounded-full bg-white/85 px-2 py-0.5 text-[10px] font-medium text-slate-800">
                    Compounding
                  </span>
                </div>
                <div className="mt-3 w-full max-w-[80%] rounded-2xl bg-white/92 px-3 py-2 text-[11px] text-slate-800 shadow-sm backdrop-blur-md">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-500">Throughput</span>
                    <span className="font-semibold text-emerald-700">{isCaCO3 ? "+18%" : "+15%"}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-[11px] text-slate-500">Resin Cost / kg</span>
                    <span className="font-semibold text-lime-700">{isCaCO3 ? "-24%" : "-20%"}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-[11px] text-slate-500">Film Break Rate</span>
                    <span className="font-semibold text-emerald-700">↓</span>
                  </div>
                  <p className="mt-2 text-[10px] leading-relaxed text-slate-600">
                    {isCaCO3
                      ? "CaCO₃ dengan kontrol distribusi ukuran partikel untuk keseimbangan kekuatan dan proses."
                      : "Na₂SO₄ Mesh 6000 untuk dispersi halus dan kestabilan dimensi produk."}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-xs text-slate-800">
              <div className="rounded-2xl border border-slate-200 bg-white p-3">
                <p className="text-[10px] text-slate-500">Typical Loading</p>
                <p className="text-sm font-semibold">{isCaCO3 ? "5–40%" : "5–30%"}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-3">
                <p className="text-[10px] text-slate-500">Mineral Purity</p>
                <p className="text-sm font-semibold">{isCaCO3 ? "≥ 98% CaCO₃" : "Mesh 6000 Na₂SO₄"}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-3">
                <p className="text-[10px] text-slate-500">Support</p>
                <p className="text-sm font-semibold">Local Lab</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SolutionsSection = ({ t }) => {
  return (
    <section id="solutions" className="bg-slate-50 px-4 py-14 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {t.solutions.title}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">{t.solutions.intro}</p>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          {t.solutions.items.map((item) => (
            <Card
              key={item.key}
              className="glass-surface-light flex h-full flex-col border-slate-200 bg-white text-slate-900"
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  {item.key === "caco3" && <Beaker className="h-4 w-4" style={{ color: BRAND_GREEN }} />}
                  {item.key === "na2so4" && <FlaskConical className="h-4 w-4 text-lime-500" />}
                  {item.key === "custom" && <Globe2 className="h-4 w-4 text-emerald-500" />}
                  <span>{item.title}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-4 text-sm text-slate-700">
                <p>{item.description}</p>
                <ul className="space-y-1.5 text-xs text-slate-700">
                  {item.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-1 h-1 w-1 rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const ApplicationsSection = ({ t }) => {
  const firstKey = t.applications.categories[0]?.key ?? "film";

  return (
    <section
      id="applications"
      className="border-y border-slate-200 bg-white px-4 py-14 text-slate-900 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {t.applications.title}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">{t.applications.intro}</p>
          </div>
        </div>

        <div className="mt-6 glass-surface-light rounded-3xl border border-slate-200 bg-slate-50 p-5">
          <Tabs defaultValue={firstKey} className="w-full">
            <TabsList className="glass-surface-light mb-4 grid w-full grid-cols-3 rounded-2xl bg-white p-1">
              {t.applications.categories.map((cat) => (
                <TabsTrigger
                  key={cat.key}
                  value={cat.key}
                  className="rounded-xl px-3 py-2 text-xs font-medium text-slate-600 data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                >
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {t.applications.categories.map((cat) => (
              <TabsContent key={cat.key} value={cat.key} className="mt-0">
                <div className="grid gap-6 md:grid-cols-[1.2fr,0.9fr]">
                  <div className="space-y-3 text-sm text-slate-700">
                    <p>{cat.description}</p>
                    <ul className="mt-3 space-y-1.5 text-xs text-slate-700">
                      {cat.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="grid gap-3 text-xs text-slate-800 md:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                      <p className="text-[11px] text-slate-500">Typical Filler Loading</p>
                      <p className="mt-1 text-sm font-semibold">5–30% (Film)</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                      <p className="text-[11px] text-slate-500">Resin Compatibility</p>
                      <p className="mt-1 text-sm font-semibold">PE / PP</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                      <p className="text-[11px] text-slate-500">Process Window</p>
                      <p className="mt-1 text-sm font-semibold">Standard Extrusion / Molding</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                      <p className="text-[11px] text-slate-500">Potential Savings*</p>
                      <p className="mt-1 text-sm font-semibold" style={{ color: BRAND_GREEN }}>
                        Up to 20–40%
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
};

const TechnologySection = ({ t }) => {
  return (
    <section
      id="technology"
      className="bg-slate-50 px-4 py-14 text-slate-900 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {t.tech.title}
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">{t.tech.intro}</p>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-4">
          {t.tech.items.map((item, index) => (
            <div
              key={item.title}
              className="glass-surface-light flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-800"
            >
              <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: BRAND_GREEN }}>
                {index === 0 && <Beaker className="h-4 w-4" />}
                {index === 1 && <FlaskConical className="h-4 w-4" />}
                {index === 2 && <ShieldCheck className="h-4 w-4" />}
                {index === 3 && <Leaf className="h-4 w-4" />}
                <span>{item.title}</span>
              </div>
              <p className="text-xs text-slate-600">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQSection = ({ t }) => {
  return (
    <section
      id="faq"
      className="border-y border-slate-200 bg-white px-4 py-14 text-slate-900 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-3xl">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {t.faq.title}
        </h2>
        <div className="mt-6 glass-surface-light rounded-3xl border border-slate-200 bg-slate-50 p-4 sm:p-6">
          <Accordion type="single" collapsible className="w-full">
            {t.faq.items.map((item, index) => (
              <AccordionItem key={item.question} value={`faq-${index}`}>
                <AccordionTrigger className="text-left text-sm font-medium text-slate-800">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-xs leading-relaxed text-slate-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

const ContactSection = ({ t, language, backendUrl }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    application: "",
    message: "",
    phone: "",
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!backendUrl) {
      window.localStorage.setItem("filler-masterbatch-contact", JSON.stringify(form));
      toast.success(t.cta.toastSuccessTitle, {
        description: t.cta.toastSuccessBody,
      });
      setForm({ name: "", company: "", email: "", application: "", message: "", phone: "" });
      return;
    }

    try {
      const payload = {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        application: form.application,
        message: form.message,
        language,
      };

      await axios.post(`${backendUrl}/api/contact-leads`, payload);

      toast.success(t.cta.toastSuccessTitle, {
        description: t.cta.toastSuccessBody,
      });
      setForm({ name: "", company: "", email: "", application: "", message: "", phone: "" });
    } catch (error) {
      window.localStorage.setItem("filler-masterbatch-contact", JSON.stringify(form));
      const descMap = {
        id: "Pengiriman tidak berhasil, data tetap kami simpan di perangkat Anda. Silakan coba kembali atau hubungi kami melalui WhatsApp.",
        en: "Submission was not successful. The data is stored in your browser. Please try again or contact us via WhatsApp.",
        zh: "提交未成功，数据已暂存于您的浏览器。请稍后重试，或通过 WhatsApp 与我们联系。",
      };
      toast.error(
        language === "id" ? "Gagal mengirim" : language === "en" ? "Submission failed" : "提交失败",
        {
          description: descMap[language] ?? descMap.id,
        },
      );
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-gradient-to-tr from-white via-emerald-50 to-lime-50 px-4 py-16 text-slate-900 sm:px-6 lg:px-8"
    >
      <div className="pointer-events-none absolute inset-0 opacity-60 mix-blend-soft-light bg-[radial-gradient(circle_at_0_0,rgba(122,159,58,0.25),transparent_55%),radial-gradient(circle_at_100%_100%,rgba(190,242,100,0.35),transparent_55%)]" />
      <div className="relative mx-auto flex max-w-6xl flex-col gap-10 lg:flex-row lg:items-start">
        <div className="flex-1 space-y-4">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {language === "id" && "Hubungi Kami"}
            {language === "en" && "Contact Us"}
            {language === "zh" && "联系我们"}
          </h2>
          <p className="max-w-xl text-sm text-slate-600">{t.cta.subtitle}</p>
          <div className="mt-3 grid gap-3 text-xs text-slate-700 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <p className="text-[11px] font-semibold" style={{ color: BRAND_GREEN }}>
                {language === "zh" ? "印尼办公室" : language === "en" ? "Indonesia Office" : "Kantor Indonesia"}
              </p>
              <p className="mt-1 leading-relaxed text-slate-600">
                PT Huatong Trading Indonesia
                <br />
                Ruko Costarica CK1 no 21 Modernland,
                <br />
                RT.001/RW.006, Klp. Indah, Kec. Tangerang,
                <br />
                Kota Tangerang, Modernland, Banten 15141
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <p className="text-[11px] font-semibold" style={{ color: BRAND_GREEN }}>
                {language === "zh" ? "联系方式" : language === "en" ? "Contact" : "Kontak"}
              </p>
              <p className="mt-1 leading-relaxed text-slate-600">
                Email: huatong8901@gmail.com
                <br />
                Phone/WhatsApp: +62 822-9836-5718
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <form
            onSubmit={handleSubmit}
            className="glass-surface-light rounded-3xl border border-emerald-100 bg-white/90 p-5 text-xs shadow-xl sm:p-6"
            noValidate
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-slate-800">
                  {t.cta.form.name}
                </label>
                <Input
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="h-8 border-emerald-100 bg-white text-xs text-slate-900 placeholder:text-slate-400 focus-visible:ring-emerald-400"
                  placeholder={t.cta.form.name}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-slate-800">
                  {t.cta.form.company}
                </label>
                <Input
                  value={form.company}
                  onChange={(e) => handleChange("company", e.target.value)}
                  className="h-8 border-emerald-100 bg-white text-xs text-slate-900 placeholder:text-slate-400 focus-visible:ring-emerald-400"
                  placeholder={t.cta.form.company}
                />
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-slate-800">
                  {t.cta.form.email}
                </label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="h-8 border-emerald-100 bg-white text-xs text-slate-900 placeholder:text-slate-400 focus-visible:ring-emerald-400"
                  placeholder={t.cta.form.email}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-medium text-slate-800">Phone / WhatsApp</label>
                <Input
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="h-8 border-emerald-100 bg-white text-xs text-slate-900 placeholder:text-slate-400 focus-visible:ring-emerald-400"
                  placeholder={language === "zh" ? "可联系的电话号码" : language === "en" ? "Reachable phone number" : "Nomor yang bisa dihubungi"}
                />
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              <label className="text-[11px] font-medium text-slate-800">
                {t.cta.form.application}
              </label>
              <Select
                value={form.application}
                onValueChange={(value) => handleChange("application", value)}
              >
                <SelectTrigger className="h-8 border-emerald-100 bg-white text-xs text-slate-900 focus-visible:ring-emerald-400">
                  <SelectValue placeholder={t.cta.form.application} />
                </SelectTrigger>
                <SelectContent className="bg-white text-xs text-slate-900">
                  <SelectItem value="film">
                    {language === "zh" ? "薄膜与包装" : language === "en" ? "Film & Packaging" : "Film & Packaging"}
                  </SelectItem>
                  <SelectItem value="injection">
                    {language === "zh" ? "注塑制品" : language === "en" ? "Injection Molding" : "Injection Molding"}
                  </SelectItem>
                  <SelectItem value="blow">
                    {language === "zh" ? "中空吹塑" : language === "en" ? "Blow Molding" : "Blow Molding"}
                  </SelectItem>
                  <SelectItem value="other">
                    {language === "zh" ? "其他塑料应用" : language === "en" ? "Other Plastic Application" : "Aplikasi plastik lainnya"}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3 space-y-1.5">
              <label className="text-[11px] font-medium text-slate-800">
                {t.cta.form.message}
              </label>
              <Textarea
                value={form.message}
                onChange={(e) => handleChange("message", e.target.value)}
                className="min-h-[80px] border-emerald-100 bg-white text-xs text-slate-900 placeholder:text-slate-400 focus-visible:ring-emerald-400"
                placeholder={t.cta.form.message}
              />
            </div>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[10px] leading-relaxed text-slate-500">
                *Indicative cost saving depends on resin price, formulation, and application.
              </p>
              <Button
                type="submit"
                className="rounded-full bg-slate-900 px-6 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
              >
                {t.cta.form.submit}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ t }) => {
  return (
    <footer className="border-t border-slate-200 bg-white px-4 py-6 text-[11px] text-slate-500 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2">
          <img src="/ht-logo.png" alt="PT Huatong Trading Indonesia" className="h-5 w-5 object-contain" />
          <span>
            © {new Date().getFullYear()} {t.meta.companyName}. All rights reserved.
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>ISO-ready quality culture</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <Globe2 className="h-3.5 w-3.5" />
            <span>Jakarta · Indonesia</span>
          </span>
        </div>
      </div>
    </footer>
  );
};

const LandingPage = ({ backendUrl }) => {
  const [language, setLanguage] = useState("id");
  const t = CONTENT[language] ?? CONTENT.id;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header language={language} onLanguageChange={setLanguage} t={t} />
      <main>
        <HeroSection t={t} />
        <SolutionsSection t={t} />
        <ApplicationsSection t={t} />
        <TechnologySection t={t} />
        <FAQSection t={t} />
        <ContactSection t={t} language={language} backendUrl={backendUrl} />
      </main>
      <Footer t={t} />
    </div>
  );
};

export default LandingPage;
